import {
  computed,
  inject,
  Injectable,
  PLATFORM_ID,
  signal,
} from "@angular/core";
import { isPlatformBrowser } from "@angular/common";
import { environment } from "../../../environments/env";

export type ConsentCategory =
  "necessary" | "performance" | "functionality" | "advertising";

export interface ConsentRecord {
  /** Opaque string — compared with exact string equality, never parsed as date. */
  policyVersion: string;
  /** ISO 8601 timestamp of when the user made their decision. */
  decidedAt: string;
  /** Per-category consent flags. `necessary` is always true. */
  categories: Record<ConsentCategory, boolean>;
}

const STORAGE_KEY = "magroConsent";
const LEGACY_KEY = "cookieBannerReaded";

@Injectable({
  providedIn: "root",
})
export class CookieConsentService {
  private readonly platformId = inject(PLATFORM_ID);

  /**
   * COOKIE_POLICY_VERSION is sourced from the environment so re-prompting
   * happens automatically after each policy update.
   */
  readonly COOKIE_POLICY_VERSION = environment.fechaUltimaActualizacionCookies;

  // ── Internal state ───────────────────────────────────────────────────────
  // NOTE: never read localStorage in a field initializer — SSR-unsafe.
  private readonly _record = signal<ConsentRecord | null>(null);
  private readonly _hydrated = signal(false);

  /** Signal consumers can use to open the preferences panel from anywhere. */
  readonly openPreferences = signal(false);

  // ── Public read-only record ──────────────────────────────────────────────
  readonly record = this._record.asReadonly();

  // ── Per-category accessors ───────────────────────────────────────────────
  /** Necessary is always true and cannot be revoked. */
  readonly necessary = computed(() => true);
  readonly performance = computed(
    () => this._record()?.categories.performance ?? false,
  );
  readonly functionality = computed(
    () => this._record()?.categories.functionality ?? false,
  );
  readonly advertising = computed(
    () => this._record()?.categories.advertising ?? false,
  );

  // ── Derived state ────────────────────────────────────────────────────────
  readonly hasDecided = computed(() => this._record() !== null);

  /**
   * True only after browser hydration and only when the user still needs to
   * decide (or re-decide after a policy version change). This prevents any
   * flash of the banner on server-rendered HTML.
   */
  readonly showBanner = computed(
    () => this._hydrated() && (!this.hasDecided() || this.needsReprompt()),
  );

  // ── Hydration ────────────────────────────────────────────────────────────

  /**
   * Reads localStorage exactly once, in the browser only.
   * Called from ml-cookie-consent-banner's afterNextRender — never in a
   * constructor or field initializer (SSR-neutral).
   */
  hydrate(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    if (this._hydrated()) return; // idempotent

    const raw = localStorage.getItem(STORAGE_KEY);

    if (!raw) {
      // Legacy migration: old flag never captured categories — treat as undecided.
      if (localStorage.getItem(LEGACY_KEY)) {
        localStorage.removeItem(LEGACY_KEY);
      }
      this._hydrated.set(true);
      return;
    }

    try {
      const parsed: ConsentRecord = JSON.parse(raw);
      if (parsed && parsed.policyVersion && parsed.categories) {
        this._record.set(parsed);
      }
    } catch {
      // Malformed JSON — treat as undecided
    }

    this._hydrated.set(true);
  }

  // ── Version check ────────────────────────────────────────────────────────

  /**
   * Returns true when the stored record exists but was made under a different
   * policy version. Uses exact string comparison — never date math.
   */
  needsReprompt(): boolean {
    const r = this._record();
    return r !== null && r.policyVersion !== this.COOKIE_POLICY_VERSION;
  }

  // ── Public actions ───────────────────────────────────────────────────────

  /** Grant all non-necessary categories. */
  acceptAll(): void {
    this.persist({
      necessary: true,
      performance: true,
      functionality: true,
      advertising: true,
    });
  }

  /** Revoke all non-necessary categories. */
  rejectNonEssential(): void {
    this.persist({
      necessary: true,
      performance: false,
      functionality: false,
      advertising: false,
    });
  }

  /**
   * Save a partial set of category preferences.
   * `necessary` is always forced to true regardless of the input.
   */
  save(categories: Partial<Record<ConsentCategory, boolean>>): void {
    this.persist({
      necessary: true,
      performance: categories.performance ?? false,
      functionality: categories.functionality ?? false,
      advertising: categories.advertising ?? false,
    });
  }

  /** Equivalent to rejectNonEssential. Kept for explicit intent from footer flow. */
  withdraw(): void {
    this.rejectNonEssential();
    this.openPreferences.set(false);
  }

  /** Open the preferences panel (e.g. from the footer link). */
  reopen(): void {
    this.openPreferences.set(true);
  }

  /**
   * Returns true when saving `next` would constitute a withdrawal of any
   * previously granted category. The banner uses this to trigger a page
   * reload so already-loaded scripts are unloaded on next navigation.
   *
   * Already-loaded scripts (Hotjar, TikTok, Meta) cannot be reliably torn
   * down in-session — a hard reload is the only vendor-agnostic guarantee.
   */
  withdrawalRequiresReload(
    prev: Partial<Record<ConsentCategory, boolean>>,
    next: Partial<Record<ConsentCategory, boolean>>,
  ): boolean {
    const cats: ConsentCategory[] = [
      "performance",
      "functionality",
      "advertising",
    ];
    return cats.some((c) => prev[c] === true && next[c] === false);
  }

  // ── Private ──────────────────────────────────────────────────────────────

  private persist(cats: Record<ConsentCategory, boolean>): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const record: ConsentRecord = {
      policyVersion: this.COOKIE_POLICY_VERSION,
      decidedAt: new Date().toISOString(),
      categories: cats,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    this._record.set(record);
    // Clean up legacy flag if still present
    localStorage.removeItem(LEGACY_KEY);
  }
}
